import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
  "inline-flex items-center gap-2 justify-center whitespace-nowrap rounded-md font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 transition-colors duration-300 ease-in-out",
  {
    variants: {
      variant: {
        default:
          "bg-n-secondary rounded-full text-n-primary font-semibold hover:bg-n-secondary/70 hover:text-n-green",
        border:
          "border-2 border-n-green-3 hover:border-n-green-4 text-n-green-4 font-semibold  rounded-full hover:bg-n-green-3/10 ",
        outline:
          "border-2 border-n-secondary text-n-secondary shadow-sm hover:bg-n-bg-1/10 rounded-full hover:text-n-secondary/80",
        green:
          "bg-n-green-s-light rounded-full text-n-primary font-semibold hover:bg-n-green-s-light/80",

        input: "border-2 border-neutral-300 bg-n-bg-2 text-n-text rounded-xl",
        ghost: "text-n-primary hover:bg-n-bg-1/10 hover:text-n-primary",
        link: "text-n-primary underline-offset-4 hover:underline hover:text-n-primary/60",
      },
      size: {
        default:
          "md:px-[24px] px-5 md:py-3 py-1 h-10 md:h-auto text-base [&_svg]:w-[20px] [&_svg]:h-[20px] md:[&_svg]:w-[24px] [&_svg]:h-[24px]",
        small: "md:px-5 py-[6px] px-3 text-base",
        input: "h-[48px] px-5 text-base",
        xs: "px-4 py-2 text-xs font-base",
        sm: "py-2 px-5 text-sm",
        md: "py-3 px-4 text-base",
        lg: "py-3 px-5",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
